import { Controller } from "@hotwired/stimulus"

/**
 * Balance Sheet Accordion Controller
 *
 * Handles collapsible rows for hierarchical balance sheet display.
 * Supports lazy loading of subclasses and accounts via Turbo Frames.
 */
export default class extends Controller {
  static targets = ["row", "toggleIcon", "childrenContainer", "childrenTable"]
  static values = {
    expanded: { type: Boolean, default: false },
    level: String, // 'class', 'subclass'
    loaded: { type: Boolean, default: false },
    url: String
  }

  connect() {
    this.updateIcon()
  }

  async toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.expandedValue) {
      this.collapse()
    } else {
      await this.expand()
    }
  }

  async expand() {
    // If not loaded yet, fetch the data
    if (!this.loadedValue && this.hasUrlValue) {
      await this.loadChildren()
    }

    this.expandedValue = true
    this.showChildren()
    this.updateIcon()
  }

  collapse() {
    this.expandedValue = false
    this.hideChildren()
    this.updateIcon()
  }

  async loadChildren() {
    if (this.loadedValue || !this.hasChildrenContainerTarget) return

    // Use childrenTable if available, otherwise fall back to childrenContainer
    const insertTarget = this.hasChildrenTableTarget ? this.childrenTableTarget : this.childrenContainerTarget

    insertTarget.innerHTML = `
      <tbody>
        <tr>
          <td colspan="9" class="px-6 py-3">
            <div class="flex items-center justify-center text-gray-400">
              <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm">Chargement...</span>
            </div>
          </td>
        </tr>
      </tbody>
    `

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const html = await response.text()
        insertTarget.innerHTML = html
        this.loadedValue = true
      } else {
        insertTarget.innerHTML = `
          <tbody>
            <tr>
              <td colspan="9" class="px-6 py-3 text-center text-red-500 text-sm">
                Erreur lors du chargement
              </td>
            </tr>
          </tbody>
        `
      }
    } catch (error) {
      console.error("Error loading children:", error)
      insertTarget.innerHTML = `
        <tbody>
          <tr>
            <td colspan="9" class="px-6 py-3 text-center text-red-500 text-sm">
              Erreur de connexion
            </td>
          </tr>
        </tbody>
      `
    }
  }

  showChildren() {
    if (this.hasChildrenContainerTarget) {
      this.childrenContainerTarget.classList.remove("hidden")
    }
  }

  hideChildren() {
    if (this.hasChildrenContainerTarget) {
      this.childrenContainerTarget.classList.add("hidden")
    }
  }

  updateIcon() {
    if (!this.hasToggleIconTarget) return

    if (this.expandedValue) {
      this.toggleIconTarget.classList.add("rotate-90")
    } else {
      this.toggleIconTarget.classList.remove("rotate-90")
    }
  }
}
